import {
  Subscription,
  SubscriptionStatus,
  CreateCheckoutSessionRequest,
  CreateCheckoutSessionResponse,
  CreatePortalSessionResponse,
  VerifySessionResponse,
} from "../types/subscription";
import { ApiError } from "./ApiError";

/**
 * Get current user subscription status
 */
export const getSubscriptionStatus = async (
  userToken: string
): Promise<SubscriptionStatus> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/status`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Create Stripe checkout session for subscription
 */
export const createCheckoutSession = async (
  request: CreateCheckoutSessionRequest,
  userToken: string
): Promise<CreateCheckoutSessionResponse> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/create-checkout-session`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || `Request failed: ${response.status}`,
      response.status,
      errorData.code
    );
  }

  return response.json();
};

/**
 * Create a Stripe billing portal session for managing the card on file. This is
 * the only way to fix a failed payment: Stripe blocks a past_due customer from
 * completing a new checkout, so that route is a dead end for them.
 */
export const createPortalSession = async (
  userToken: string
): Promise<CreatePortalSessionResponse> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/create-portal-session`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || `Request failed: ${response.status}`,
      response.status,
      errorData.code
    );
  }

  return response.json();
};

/**
 * Verify a checkout session and proactively sync subscription status.
 * Eliminates the webhook timing gap after Stripe checkout completion.
 */
export const verifyCheckoutSession = async (
  sessionId: string,
  userToken: string
): Promise<VerifySessionResponse> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/verify-session`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({ sessionId }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed: ${response.status}`);
  }

  return response.json();
};

/**
 * Cancel subscription (will cancel at end of period)
 */
export const cancelSubscription = async (userToken: string): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/cancel`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed: ${response.status}`);
  }
};

/**
 * Reactivate a canceled subscription
 */
export const reactivateSubscription = async (
  userToken: string
): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/reactivate`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed: ${response.status}`);
  }
};

/**
 * Cancel a free trial. There's no card on file, so this records the request,
 * stops the trial emails, and switches the billing page over to a "you won't be
 * charged" message — access still runs to the end of the trial.
 */
export const cancelTrial = async (
  userToken: string,
  reason?: string
): Promise<{ subscription?: Subscription }> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/cancel-trial`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify(reason ? { reason } : {}),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || `Request failed: ${response.status}`,
      response.status,
      errorData.code
    );
  }

  return response.json();
};

/**
 * Undo a trial cancellation while the trial is still running.
 */
export const resumeTrial = async (
  userToken: string
): Promise<{ subscription?: Subscription }> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/resume-trial`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.error || `Request failed: ${response.status}`,
      response.status,
      errorData.code
    );
  }

  return response.json();
};

/**
 * Change subscription plan (monthly <-> annual)
 */
export const changePlan = async (
  planType: 'monthly' | 'annual',
  userToken: string
): Promise<void> => {
  const response = await fetch(
    `${process.env.REACT_APP_API_URL}/api/subscription/change-plan`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({ planType }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed: ${response.status}`);
  }
};